import { startAuthentication } from '@simplewebauthn/browser';

import { AuthErrorMessages, ROUTES, readSignupIntentParams, resolveSignupIntentDestination } from '@repo/common';
import {
  isSafeDestinationPath,
  persistPendingDestination,
  readPendingDestination,
  readSignupIntent,
} from '@repo/frontend-shared/utils';

import { captureSignupAttribution } from './signup-attribution';

/**
 * Opens OAuth authorization URL with proper parameters
 *
 * NEW FLOW (OAuth 2.1 with PKCE):
 * - Sends originalUri and redirectUri as direct query parameters
 * - Backend generates cryptographic state and stores it in Redis
 * - Backend generates PKCE codes for enhanced security
 * - No client-side state encoding (more secure)
 */
export const openAuthUrl = async (original: string, redirect: string, endpoint: string) => {
  const originalUri = `${window.location.origin}${original}`;
  const redirectUri = `${window.location.origin}${redirect}`;

  const params = new URLSearchParams({
    originalUri,
    redirectUri,
    signupAttribution: JSON.stringify(captureSignupAttribution()),
  });

  window.open(`${endpoint}?${params.toString()}`, '_self');
};

export const openAuthUrlFromLocation = async (location: Pick<Location, 'pathname' | 'search'>, endpoint: string) => {
  const params = new URLSearchParams(location.search);
  const redirectParam = params.get('redirect');
  const pending = isSafeDestinationPath(redirectParam) ? redirectParam : readPendingDestination();

  if (pending) {
    persistPendingDestination(pending);
  }

  const redirect = pending || ROUTES.HOME;
  const original = location.pathname + location.search;

  await openAuthUrl(original, redirect, endpoint);
};

export async function openGoogleAccountReauthUrlFromLocation(
  location: Pick<Location, 'pathname' | 'search'>,
): Promise<void> {
  const originalUri = `${window.location.origin}${location.pathname}${location.search}`;
  const response = await fetch('/auth/google/connect', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ originalUri, redirectUri: originalUri }),
  });
  const payload: unknown = await response.json();
  const authUrl = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'authUrl') : undefined;
  if (!response.ok || typeof authUrl !== 'string') {
    const message = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'message') : undefined;
    throw new Error(typeof message === 'string' ? message : 'Google verification could not be started');
  }
  window.open(authUrl, '_self');
}

type BetterAuthSocialProvider = 'google' | 'linkedin';
export type BetterAuthSocialIntent = 'sign-in' | 'sign-up';

type BetterAuthUser = { id: string; email: string; name: string };
export type BetterAuthSignInResult =
  | { kind: 'signed-in'; user: BetterAuthUser }
  | { kind: 'mfa'; methods: readonly ('totp' | 'otp')[] };

async function betterAuthJson(
  path: string,
  body?: Record<string, unknown>,
  captchaResponse?: string,
): Promise<unknown> {
  const response = await fetch(`/auth/better${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'include',
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(captchaResponse ? { 'x-captcha-response': captchaResponse } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'message') : undefined;
    throw new Error(
      message === 'Invalid email or password'
        ? AuthErrorMessages.INVALID_CREDENTIALS
        : typeof message === 'string'
          ? message
          : 'Authentication failed. Please try again.',
    );
  }
  return payload;
}

function readBetterAuthUser(payload: unknown): BetterAuthUser {
  const user = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'user') : undefined;
  const id = typeof user === 'object' && user !== null ? Reflect.get(user, 'id') : undefined;
  const email = typeof user === 'object' && user !== null ? Reflect.get(user, 'email') : undefined;
  const name = typeof user === 'object' && user !== null ? Reflect.get(user, 'name') : undefined;
  if (typeof id !== 'string' || typeof email !== 'string') throw new Error('Authentication response was incomplete');
  return { id, email, name: typeof name === 'string' ? name : '' };
}

export async function betterAuthSignInEmail(
  email: string,
  password: string,
  captchaResponse?: string,
): Promise<BetterAuthSignInResult> {
  const payload = await betterAuthJson('/sign-in/email', { email, password }, captchaResponse);
  const requiresMfa =
    typeof payload === 'object' && payload !== null && Reflect.get(payload, 'twoFactorRedirect') === true;
  if (requiresMfa) {
    const rawMethods = Reflect.get(payload, 'twoFactorMethods');
    const methods = Array.isArray(rawMethods)
      ? rawMethods.filter((method): method is 'totp' | 'otp' => method === 'totp' || method === 'otp')
      : [];
    return { kind: 'mfa', methods };
  }
  return { kind: 'signed-in', user: readBetterAuthUser(payload) };
}

export async function betterAuthVerifyTotp(code: string): Promise<BetterAuthUser> {
  return readBetterAuthUser(await betterAuthJson('/two-factor/verify-totp', { code }));
}

export async function betterAuthSendOtp(): Promise<void> {
  await betterAuthJson('/two-factor/send-otp', {});
}

export async function betterAuthVerifyOtp(code: string): Promise<BetterAuthUser> {
  return readBetterAuthUser(await betterAuthJson('/two-factor/verify-otp', { code }));
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value.trim());
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function betterAuthVerifyBackupCode(code: string): Promise<BetterAuthUser> {
  return readBetterAuthUser(await betterAuthJson('/two-factor/verify-backup-code', { code: await sha256Hex(code) }));
}

export async function betterAuthRegenerateBackupCodes(password: string): Promise<readonly string[]> {
  const payload = await betterAuthJson('/two-factor/generate-backup-codes', { password });
  const codes = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'backupCodes') : undefined;
  if (!Array.isArray(codes) || codes.some(code => typeof code !== 'string')) {
    throw new Error('Replacement recovery codes could not be generated');
  }
  return codes;
}

export async function betterAuthSignInPasskey(): Promise<BetterAuthUser> {
  const options = await betterAuthJson('/passkey/generate-authenticate-options');
  if (typeof options !== 'object' || options === null) throw new Error('Passkey challenge was invalid');
  const response = await startAuthentication({
    optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
  });
  const { id, rawId, response: authenticatorResponse, type, authenticatorAttachment } = response;
  const responseBody = { id, rawId, response: authenticatorResponse, type, authenticatorAttachment };
  return readBetterAuthUser(await betterAuthJson('/passkey/verify-authentication', { response: responseBody }));
}

export async function betterAuthSignOut(): Promise<void> {
  await betterAuthJson('/sign-out', {});
}

export async function betterAuthRevokeOtherSessions(): Promise<void> {
  await betterAuthJson('/revoke-other-sessions', {});
}

export async function openBetterAuthSocialUrlFromLocation(
  location: Pick<Location, 'pathname' | 'search'>,
  provider: BetterAuthSocialProvider,
  captchaResponse?: string,
  intent: BetterAuthSocialIntent = 'sign-in',
  // Each app's dashboard. Not '/': on the shared web host that is the landing site, outside the member app.
  fallbackDestination: string = ROUTES.DASHBOARD,
): Promise<void> {
  const params = new URLSearchParams(location.search);
  const redirectParam = params.get('redirect');
  const pending = isSafeDestinationPath(redirectParam) ? redirectParam : readPendingDestination();
  const redirect = pending || fallbackDestination;
  persistPendingDestination(redirect);
  const signupIntent = intent === 'sign-up' ? (readSignupIntentParams(params) ?? readSignupIntent()) : undefined;
  const newUserDestination = signupIntent ? resolveSignupIntentDestination(signupIntent) : undefined;

  const response = await fetch('/auth/better/sign-in/social', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(captchaResponse ? { 'x-captcha-response': captchaResponse } : {}),
    },
    body: JSON.stringify({
      provider,
      callbackURL: `${window.location.origin}${redirect}`,
      errorCallbackURL: `${window.location.origin}${location.pathname}${location.search}`,
      ...(intent === 'sign-up'
        ? {
            requestSignUp: true,
            // A new member goes where they were headed (the dashboard by default); onboarding questions follow inside it.
            newUserCallbackURL: `${window.location.origin}${newUserDestination ?? redirect}`,
            additionalData: {
              signupAttribution: JSON.stringify(captureSignupAttribution()),
              signupIntent: signupIntent?.intent,
              signupPlan: signupIntent?.plan,
            },
          }
        : {}),
    }),
  });
  const payload: unknown = await response.json();
  const url = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'url') : undefined;
  if (!response.ok || typeof url !== 'string') throw new Error('Social login could not start');
  window.open(url, '_self');
}
